import Head from 'next/head';

import { HOMEPAGE_URL } from '../../../helpers/globals';
import { buildHomesMarketSeo, pickHomesMarketHeroPhoto } from '../homesMarketSeo';
import type { ListingCardData } from '../property/types';

interface DirectorySeoHeadProps {
  canonicalUrl: string;
  listings: ListingCardData[];
  locationName: string;
  seoDescription: string;
  seoTitle: string;
  totalCount: number;
}

export const DirectorySeoHead: React.FC<DirectorySeoHeadProps> = ({
  canonicalUrl,
  listings,
  locationName,
  seoDescription,
  seoTitle,
  totalCount,
}) => {
  const canonicalPath = new URL(canonicalUrl).pathname;
  const marketSlug = canonicalPath.split('/').filter(Boolean).at(-1) ?? '';
  const seo = buildHomesMarketSeo({
    canonicalPath,
    heroPhotoUrl: pickHomesMarketHeroPhoto(listings.flatMap((listing) => listing.photos ?? [])),
    isHubPage: false,
    listingCount: null,
    locationText: locationName,
    marketDisplayName: locationName,
    marketSlug,
    origin: HOMEPAGE_URL,
  });
  const ogImage = seo.ogImageUrl;

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: listings.slice(0, 10).map((listing, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: {
        '@type': 'Product',
        name: listing.fullAddress,
        url: `${HOMEPAGE_URL}/properties/${listing.slug}/aid_${listing.addressToken}`,
        offers: {
          '@type': 'Offer',
          price: listing.listPrice,
          priceCurrency: 'USD',
        },
      },
    })),
  };

  const breadcrumbItems = [
    { '@type': 'ListItem', position: 1, name: 'Homes', item: `${HOMEPAGE_URL}/homes` },
  ];

  breadcrumbItems.push({
    '@type': 'ListItem',
    position: 2,
    name: locationName,
    item: canonicalUrl,
  });

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  };

  return (
    <Head>
      <title key="title">{seoTitle}</title>
      <meta key="description" name="description" content={seoDescription} />
      <link key="canonical" rel="canonical" href={canonicalUrl} />

      <meta key="og:title" property="og:title" content={seoTitle} />
      <meta key="og:description" property="og:description" content={seoDescription} />
      <meta key="og:url" property="og:url" content={canonicalUrl} />
      <meta key="og:type" property="og:type" content="website" />
      <meta key="og:site_name" property="og:site_name" content="Opendoor" />
      <meta key="og:image" property="og:image" content={ogImage} />
      <meta key="og:image:secure_url" property="og:image:secure_url" content={ogImage} />
      <meta key="og:image:alt" property="og:image:alt" content={seo.ogImageAlt} />
      <meta key="og:image:width" property="og:image:width" content="1200" />
      <meta key="og:image:height" property="og:image:height" content="630" />

      <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
      <meta key="twitter:url" property="twitter:url" content={canonicalUrl} />
      <meta key="twitter:title" name="twitter:title" content={seoTitle} />
      <meta key="twitter:description" name="twitter:description" content={seoDescription} />
      <meta key="twitter:image" name="twitter:image" content={ogImage} />
      <meta key="twitter:image:alt" name="twitter:image:alt" content={seo.ogImageAlt} />

      {listings.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }}
        />
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      {totalCount === 0 && <meta key="robots" name="robots" content="noindex" />}
    </Head>
  );
};
